import { useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  FormattedInput,
  HiddenLabel,
  IconButton,
  Input,
  Label,
  NumberFormatter,
  PopoverOutlet,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@figma/fpl-components';
import { Icon24Refresh } from '@figma/fpl-icons';
import cssVars from '@figma/fpl-tokens/css-vars';
import { DrawStyleSlider } from '@/UI/Slider/DrawStyleSlider';
import { I18N_KEYS } from '@/language/keys';
import { styles } from './ToolInputControls.styles';
import type { ReactNode } from 'react';

export interface ControlProps {
  id: string;
  label: string;
  disabled: boolean;
  error?: string;
}

export function ToolInputRow({
  label,
  required,
  disabled,
  description,
  headerActions,
  children,
}: {
  label: ReactNode;
  required?: boolean;
  disabled: boolean;
  description?: string;
  headerActions?: ReactNode;
  children?: ReactNode;
}) {
  const actions = headerActions ? <Stack.Row {...stylex.props(styles.headerActions)}>{headerActions}</Stack.Row> : null;

  return (
    <Stack.Col gap={cssVars.spacer.x2} {...stylex.props(styles.inputRow)}>
      <Stack.Col>
        <Stack.Row gap={cssVars.spacer.x2}>
          <Stack.Row gap={cssVars.spacer.x2} {...stylex.props(styles.label)}>
            {typeof label === 'string' ? (
              <Text.div variant="body-medium" color={disabled ? 'disabled' : 'default'}>
                {required ? `${label} *` : label}
              </Text.div>
            ) : (
              label
            )}
          </Stack.Row>
          {actions}
        </Stack.Row>
        {description && (
          <Text.div variant="body-medium" color={disabled ? 'disabled' : 'secondary'}>
            {description}
          </Text.div>
        )}
      </Stack.Col>
      {children && <Stack.Col>{children}</Stack.Col>}
    </Stack.Col>
  );
}

function InputError({ error, id }: Pick<ControlProps, 'error'> & { id?: string }) {
  return error ? (
    <Text.span id={id} variant="body-medium" color="danger" role="alert" {...stylex.props(styles.inputError)}>
      {error}
    </Text.span>
  ) : null;
}

export function ToolTextInput({
  id,
  label,
  value,
  onChange,
  onBlur,
  disabled,
  error,
}: ControlProps & {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
}) {
  // FPL draws the outline on Root. Its Textarea props disallow aria-invalid,
  // so the ref sets the accessibility state on the native textarea.
  return (
    <>
      <Textarea.Root {...stylex.props(styles.resizableTextInput, !!error && styles.invalidText)}>
        <Textarea
          id={id}
          aria-label={label}
          ref={(element) => element?.setAttribute('aria-invalid', String(!!error))}
          aria-describedby={error ? `${id}-error` : undefined}
          value={value}
          onChange={onChange}
          onBlur={disabled ? undefined : onBlur}
          disabled={disabled}
          rows={5}
        />
      </Textarea.Root>
      <InputError id={`${id}-error`} error={error} />
    </>
  );
}

export function ToolBooleanInput({
  label,
  value,
  onChange,
  disabled,
  error,
  description,
}: ControlProps & {
  value: boolean;
  onChange: (value: boolean) => void;
  description?: string;
}) {
  return (
    <ToolInputRow
      label={<Switch label={<Label>{label}</Label>} checked={value} onChange={onChange} disabled={disabled} />}
      disabled={disabled}
      description={description}
    >
      {error && <InputError error={error} />}
    </ToolInputRow>
  );
}

export function ToolDropdownInput({
  label,
  value,
  options,
  onChange,
  disabled,
  error,
}: ControlProps & {
  value: number;
  options: (string | number)[];
  onChange: (value: number) => void;
}) {
  return (
    // Keep the menu in its host's stacking context, including the legacy parameters popover.
    <PopoverOutlet>
      <Select.Root value={String(value)} onChange={(next) => onChange(Number(next))}>
        <Select.Trigger label={<HiddenLabel>{label}</HiddenLabel>} width="fill" disabled={disabled} />
        <Select.Container>
          {options.map((option, index) => (
            <Select.Option key={index} value={String(index)} title={String(option)}>
              {String(option)}
            </Select.Option>
          ))}
        </Select.Container>
      </Select.Root>
      <InputError error={error} />
    </PopoverOutlet>
  );
}

export function ToolArrayInput({
  value,
  onChange,
  ...props
}: ControlProps & {
  value: string | string[];
  onChange: (value: string | string[]) => void;
}) {
  const { t } = useTranslation();
  const text = Array.isArray(value) ? value.join(', ') : value;
  return (
    <>
      <ToolTextInput
        {...props}
        value={text}
        onChange={onChange}
        onBlur={() =>
          onChange(
            text
              .split(',')
              .map((entry) => entry.trim())
              .filter(Boolean),
          )
        }
      />
      <Text.div variant="body-medium" color={props.disabled ? 'disabled' : 'secondary'}>
        {t(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.ARRAY_HELPER)}
      </Text.div>
    </>
  );
}

class ToolNumberFormatter extends NumberFormatter {
  constructor(bigStep: number, min?: number, max?: number) {
    super({ min, max, nudge: 1, bigNudge: bigStep, maximumFractionDigits: 0 });
  }

  override parse(text: string): number {
    return Math.round(super.parse(text));
  }
}

type NumberControlProps = ControlProps & {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
};

function ToolNumberField({
  id,
  label,
  value,
  onChange,
  disabled,
  error,
  min,
  max,
  bigStep,
}: NumberControlProps & { bigStep: number }) {
  const formatter = useMemo(() => new ToolNumberFormatter(bigStep, min, max), [bigStep, min, max]);
  return (
    <FormattedInput.Root>
      <FormattedInput.Field<number>
        id={id}
        aria-label={label}
        aria-invalid={!!error}
        formatter={formatter}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </FormattedInput.Root>
  );
}

export function ToolNumberInput(props: NumberControlProps) {
  const { label, value, onChange, disabled, error, min: minProp, max: maxProp } = props;
  // Match Figma's IntegerInput: only a complete range constrains typed values.
  const hasExplicitRange = minProp !== undefined && maxProp !== undefined;
  const min = hasExplicitRange ? minProp : 0;
  const max = hasExplicitRange ? maxProp : 100;
  // Figma uses roughly 10% of the range, rounded to a whole step, with at least one step.
  const bigStep = Math.max(1, Math.round((max - min) / 10));
  const sliderValue = Math.min(max, Math.max(min, Number.isNaN(value) ? min : value));
  return (
    <>
      <div style={{ width: min < max ? '100%' : 56 }}>
        <Input.Group columns={min < max ? '1fr 56px' : '56px'}>
          {min < max && (
            <div {...stylex.props(styles.sliderWrapper)}>
              <DrawStyleSlider
                aria-label={label}
                value={sliderValue}
                min={min}
                max={max}
                step={1}
                bigStep={bigStep}
                rangeAnchor={min}
                onChange={(next) => onChange(Math.round(next))}
                disabled={disabled}
              />
            </div>
          )}
          <ToolNumberField
            {...props}
            min={hasExplicitRange ? min : undefined}
            max={hasExplicitRange ? max : undefined}
            bigStep={bigStep}
          />
        </Input.Group>
      </div>
      <InputError error={error} />
    </>
  );
}

// Match Figma's SeedInput normalization, including manually entered seeds beyond the generated range.
function normalizeFixedSeed(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) return 0;
  return Math.min(Number.MAX_SAFE_INTEGER, Math.max(0, Math.round(value)));
}

class FixedSeedFormatter extends NumberFormatter<number> {
  constructor() {
    super({ min: 0, max: Number.MAX_SAFE_INTEGER, nudge: 1, maximumFractionDigits: 0 });
  }

  override parse(value: string): number {
    return normalizeFixedSeed(super.parse(value));
  }
}

const FIXED_SEED_FORMATTER = new FixedSeedFormatter();
const getRandomSeed = () => Math.floor(Math.random() * 1_000_000) + 1;

export function ToolSeedInput({
  id,
  label,
  value,
  onChange,
  disabled,
  error,
}: ControlProps & {
  value: { seed?: number; isRandom: boolean };
  onChange: (value: { seed: number; isRandom: boolean }) => void;
}) {
  const { t } = useTranslation();
  const fixedSeed = normalizeFixedSeed(value.seed);
  return (
    <>
      <Stack.Row gap={cssVars.spacer.x2} align="center">
        <Checkbox
          label={<Label>{t(I18N_KEYS.RECIPE_MAIN.NODES.SEED.RANDOM)}</Label>}
          checked={value.isRandom}
          onChange={(isRandom) =>
            onChange({ isRandom, seed: !isRandom && value.seed === undefined ? getRandomSeed() : fixedSeed })
          }
          disabled={disabled}
        />
        {!value.isRandom && (
          <div {...stylex.props(styles.fixedSeedGroup, disabled && styles.fixedSeedGroupDisabled)}>
            <Input.Group columns="1fr auto">
              <FormattedInput.Root>
                <FormattedInput.Field<number>
                  id={id}
                  aria-label={label}
                  aria-invalid={!!error}
                  formatter={FIXED_SEED_FORMATTER}
                  inputMode="numeric"
                  value={fixedSeed}
                  onChange={(seed) => onChange({ isRandom: false, seed })}
                  disabled={disabled}
                />
              </FormattedInput.Root>
              <IconButton
                aria-label={t(I18N_KEYS.RECIPE_MAIN.NODES.SEED.REFRESH)}
                onClick={() => onChange({ isRandom: false, seed: getRandomSeed() })}
                disabled={disabled}
              >
                <Icon24Refresh {...stylex.props(styles.seedRefreshIcon)} />
              </IconButton>
            </Input.Group>
          </div>
        )}
      </Stack.Row>
      <InputError error={error} />
    </>
  );
}
